import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import type { MouseEvent } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { DailyMetricPoint } from "./wearableMetricAggregation";

interface WearableMetricLineChartProps {
  color: string;
  unit: string;
  dailyPoints: DailyMetricPoint[];
  rangeDays: number;
  onDayClick?: (dateKey: string) => void;
}

interface ChartRow {
  dateKey: string;
  dateLabel: string;
  value: number | null;
  displayValue: string;
}

function CustomTooltip({
  active,
  payload,
  unit,
  showClickHint,
}: {
  active?: boolean;
  payload?: Array<{ payload?: ChartRow }>;
  unit: string;
  showClickHint: boolean;
}) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row || row.value === null) return null;

  return (
    <Box
      sx={{
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        boxShadow: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary" display="block">
        {row.dateLabel}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {row.displayValue}
        {unit ? ` ${unit}` : ""}
      </Typography>
      {showClickHint ? (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          Clique para ver este dia
        </Typography>
      ) : null}
    </Box>
  );
}

function ClickableDot({
  cx,
  cy,
  payload,
  color,
  radius,
  onDayClick,
}: {
  cx?: number;
  cy?: number;
  payload?: ChartRow;
  color: string;
  radius: number;
  onDayClick?: (dateKey: string) => void;
}) {
  if (cx == null || cy == null || !payload || payload.value === null) return null;

  const handleClick = (event: MouseEvent<SVGCircleElement>) => {
    event.stopPropagation();
    onDayClick?.(payload.dateKey);
  };

  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={color}
      stroke="none"
      style={{ cursor: onDayClick ? "pointer" : "default" }}
      onClick={onDayClick ? handleClick : undefined}
    />
  );
}

export function WearableMetricLineChart({
  color,
  unit,
  dailyPoints,
  rangeDays,
  onDayClick,
}: WearableMetricLineChartProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const chartData: ChartRow[] = dailyPoints.map((point) => ({
    dateKey: point.dateKey,
    dateLabel: point.dateLabel,
    value: point.value,
    displayValue: point.displayValue,
  }));

  const hasAnyData = chartData.some((row) => row.value !== null);
  const dotRadius = isMobile ? 8 : 5;
  const activeDotRadius = isMobile ? 11 : 8;

  if (rangeDays === 1) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px dashed",
          borderColor: "divider",
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ px: 2 }}>
          Selecione um período maior para ver tendência
        </Typography>
      </Box>
    );
  }

  if (!hasAnyData) {
    return (
      <Box
        sx={{
          height: 220,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Sem dados no período selecionado
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", height: 260, minHeight: 220 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis
            dataKey="dateLabel"
            tick={{ fontSize: isMobile ? 10 : 12 }}
            interval={isMobile ? "preserveStartEnd" : 0}
            angle={isMobile ? -35 : 0}
            textAnchor={isMobile ? "end" : "middle"}
            height={isMobile ? 48 : 32}
          />
          <YAxis
            tick={{ fontSize: 12 }}
            width={48}
            tickFormatter={(value: number) =>
              value >= 1000 ? `${(value / 1000).toFixed(1)}k` : `${value}`
            }
          />
          <Tooltip
            content={
              <CustomTooltip unit={unit} showClickHint={Boolean(onDayClick)} />
            }
          />
          <Line
            type="monotone"
            dataKey="value"
            stroke={color}
            strokeWidth={2.5}
            dot={(props) => (
              <ClickableDot
                {...props}
                color={color}
                radius={dotRadius}
                onDayClick={onDayClick}
              />
            )}
            activeDot={(props) => (
              <ClickableDot
                {...props}
                color={color}
                radius={activeDotRadius}
                onDayClick={onDayClick}
              />
            )}
            connectNulls
          />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
}
